import { useTranslations } from 'next-intl';
import React from 'react';

import { CartDto } from '@cineamo/api/models/cart/CartDto.types';

import Button from '@cineamo/ui/src/button/Button';
import { IconErrorSolid48 } from '@cineamo/ui/src/icons/ic-errorSolid';
import { IconReset16 } from '@cineamo/ui/src/icons/ic-reset';

import { useCartProvider } from '@cineamo/composables/src/providers/cartProvider';

type PaymentFailedViewProps = {
    /** Defaults to "Payment failed". */
    title?: string;
    message?: string;
    /** Shown with the support hint, so the guest can quote it. */
    orderId?: CartDto['orderId'] | null;
};

/** The verdict for an order that ended without tickets (CIN-15436): what happened, the order number, and a restart. */
const PaymentFailedView: React.FC<PaymentFailedViewProps> = ({ title, message, orderId }) => {
    const t = useTranslations('tickets');
    const { setCurrentStep, initEmptyCart } = useCartProvider();

    const restart = async () => {
        // A payment redirect's result is in the URL and would bring this view straight back.
        // Only Stripe's parameters go: others (a movie-date `token`, say) still mean something to the page.
        const url = new URL(window.location.href);
        ['payment_intent', 'payment_intent_client_secret', 'redirect_status'].forEach((key) =>
            url.searchParams.delete(key)
        );
        window.history.replaceState(null, '', url);
        // The failed cart is spent: a new booking starts from an empty one.
        await initEmptyCart();
        setCurrentStep('01_seat_picking');
    };

    return (
        <div
            role="alert"
            className="flex flex-col items-center space-y-16 container max-w-[600px] mx-auto pt-[48px] px-16">
            <div className="w-48 h-48 flex items-center justify-center rounded-full bg-black">
                <IconErrorSolid48 className="text-red" />
            </div>
            <h1 className="text-headline-24 text-center">{title ?? t('payment-failed')}</h1>
            <p className="text-body-14 font-montserrat-medium whitespace-pre-wrap text-center">
                {message ?? t('payment-failed-message')}
            </p>
            {orderId ? <p className="text-body-14 opacity-70">{t('order-number', { orderNumber: orderId })}</p> : null}
            <p className="text-body-14 text-center">{t('booking-status.support')}</p>
            <Button
                leadingIcon={<IconReset16 />}
                onClick={restart}>
                {t('restart')}
            </Button>
        </div>
    );
};

export default PaymentFailedView;
